import { useEffect, useState } from "react";
import { copy } from "../copy";
import { formatMoney } from "../format";
import { PAYOUT_COUNTER_FLOOR_CENTS, type LandingFeed, type Pick } from "../feed";
import type { BeatPlayer } from "../audio";
import ChromeWordmark from "./ChromeWordmark";
import PickCards from "./PickCards";

type Props = { feed: LandingFeed | null; player: BeatPlayer; onBack: (pick: Pick) => void; slipOpen: boolean };

const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

// Eased count-up for the payout figure, in whole dollars so the figure never
// flickers through cents. With reduced motion it shows the total at once.
function useCountUp(targetCents: number, duration = 1500): number {
  const [value, setValue] = useState(() => (prefersReducedMotion() ? targetCents : 0));
  useEffect(() => {
    if (prefersReducedMotion() || typeof requestAnimationFrame !== "function") {
      setValue(targetCents);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(progress < 1 ? Math.round((targetCents * eased) / 100) * 100 : targetCents);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [targetCents, duration]);
  return value;
}

function PayoutCounter({ cents }: { cents: number }) {
  const shown = useCountUp(cents);
  return (
    <div data-testid="payout-counter" className="mt-9 md:mt-11">
      <p className="text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{copy.hero.payoutLabel}</p>
      <p aria-live="polite" aria-atomic="true" className="bf-tabular mt-1 font-headline text-5xl leading-none text-gold md:text-6xl">
        {formatMoney(shown)}
      </p>
    </div>
  );
}

// The hero never waits on the feed: copy and calls to action paint first,
// and the pick cards and payout counter join when data arrives.
export default function Hero({ feed, player, onBack, slipOpen }: Props) {
  const hasLive = (feed?.liveContests.length ?? 0) > 0;
  const paidOut = feed?.paidOutThisWeekCents ?? 0;
  const [lead, tail] = copy.hero.headline;

  return (
    <header id="top" className="relative overflow-hidden px-4 pb-16 pt-28 md:px-6 md:pb-24 md:pt-40 lg:pt-32">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] max-w-[160vw] -translate-x-1/2 rounded-full bg-money/10 blur-3xl" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ground" />
      </div>
      {/* Drifting smoke. With reduced motion it holds still as a static haze. */}
      <div
        aria-hidden
        data-testid="smoke"
        className="smoke pointer-events-none absolute inset-0 animate-smoke motion-reduce:animate-none"
      />

      {/* Centered stack on phones and tablets. From lg the pick cards sit
          beside the copy, so they are above the fold on a laptop screen. */}
      <div className="relative mx-auto grid w-full max-w-[1240px] lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-10">
        <div className="mx-auto max-w-3xl text-center lg:mx-0 lg:text-left">
          <ChromeWordmark
            testId="wordmark"
            className="block text-[15vw] leading-[0.85] sm:text-[10vw] md:text-[112px] lg:text-[96px]"
          />

          <h1 className="mt-6 font-headline text-[42px] uppercase leading-[0.9] tracking-[-0.02em] text-chrome sm:text-6xl md:mt-8 md:text-[92px] lg:text-[76px]">
            {lead} <br />
            <span className="text-gold">{tail}</span>
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-chrome-dim md:mt-6 md:text-lg lg:mx-0">{copy.hero.sub}</p>

          <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center md:mt-10 lg:justify-start">
            <a
              href="/contests"
              className="bf-focusable flex h-12 items-center justify-center whitespace-nowrap rounded-md bg-money px-7 text-sm font-bold uppercase tracking-[0.12em] text-ground transition-colors hover:bg-money-deep"
            >
              {copy.hero.enterContest}
            </a>
            <a
              href={hasLive ? "#live-contests" : "/contests"}
              className="bf-focusable flex h-12 items-center justify-center whitespace-nowrap rounded-md border border-gold px-7 text-sm font-bold uppercase tracking-[0.12em] text-gold transition-colors hover:bg-gold/10"
            >
              {copy.hero.makePick}
            </a>
          </div>

          {paidOut >= PAYOUT_COUNTER_FLOOR_CENTS && <PayoutCounter cents={paidOut} />}
        </div>

        {feed && (
          <div className="mx-auto mt-14 w-full max-w-3xl md:mt-20 lg:mt-0">
            <PickCards contests={feed.liveContests} player={player} onBack={onBack} docked={slipOpen} />
          </div>
        )}
      </div>
    </header>
  );
}
